const DEFAULT_PUBLIC_DOCS_API_BASE_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/publicDocsApi';

const normalizeLookupValue = (value) => String(value || '').trim();

export const getPublicDocsApiBaseUrl = () => (
  normalizeLookupValue(process.env.REACT_APP_PUBLIC_DOCS_API_URL) || DEFAULT_PUBLIC_DOCS_API_BASE_URL
);

export const buildPublicDocsApiUrl = (resourceType, lookup) => {
  const baseUrl = getPublicDocsApiBaseUrl().replace(/\/+$/, '');
  const safeResourceType = normalizeLookupValue(resourceType);
  const safeLookup = encodeURIComponent(normalizeLookupValue(lookup));

  return `${baseUrl}/public/${safeResourceType}/${safeLookup}`;
};

export const extractPublicDocumentLookup = (search = '') => {
  const rawSearch = normalizeLookupValue(search).replace(/^\?/, '');
  if (!rawSearch) return '';

  if (!rawSearch.includes('=')) {
    return decodeURIComponent(rawSearch);
  }

  try {
    const params = new URLSearchParams(rawSearch);
    const keyedValue = params.get('token') || params.get('invoiceNumber') || params.get('receiptNumber');
    if (keyedValue) {
      return decodeURIComponent(keyedValue);
    }

    const firstEntry = params.entries().next();
    if (!firstEntry.done) {
      const [key, value] = firstEntry.value;
      return decodeURIComponent(value || key || '');
    }
  } catch (error) {
    return decodeURIComponent(rawSearch);
  }

  return decodeURIComponent(rawSearch);
};

export const normalizeApiTimestamp = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.toMillis === 'function') return new Date(value.toMillis());
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  if (typeof value?._seconds === 'number') return new Date(value._seconds * 1000);
  if (typeof value === 'number') return new Date(value);

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const formatApiDate = (value, fallback = '--') => {
  const parsedDate = normalizeApiTimestamp(value);
  if (!parsedDate) return fallback;

  return new Intl.DateTimeFormat('en-KE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(parsedDate);
};

export const fetchPublicDocument = async (resourceType, lookup) => {
  const response = await fetch(buildPublicDocsApiUrl(resourceType, lookup), {
    method: 'GET',
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(payload?.error || 'Unable to load the requested document.');
  }

  return payload;
};

export const fetchPublicLocation = async () => {
  return fetchPublicDocument('location', '');
};

export const fetchPublicWeather = async ({ city = 'Nairobi', aqi = 'yes', lang = '' } = {}) => {
  const baseUrl = buildPublicDocsApiUrl('weather', 'current');
  const params = new URLSearchParams();

  if (city) params.set('q', city);
  if (aqi) params.set('aqi', aqi);
  if (lang) params.set('lang', lang);

  const response = await fetch(`${baseUrl}?${params.toString()}`, {
    method: 'GET',
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(payload?.error || 'Unable to load weather information.');
  }

  return payload;
};
